import { BoundedContent, Box } from '@vp/swan';
import { __ } from '@99designs/i18n';
import { ProductOptionsQuery, ServiceDetailsPageDataQuery } from '../../../graphql';
import { FAQSection } from '../../FAQSection';
import { HowItWorks } from '../../HowItWorks';
import { Inclusions } from '../../Inclusions';
import { Inspiration } from '../../Inspiration';
import { Personalisation } from '../../RecordPersonalisation';
import { Header } from '../Header';

interface ProductModelingMainContentProps {
    serviceMpvId: string;
    productName: string;
    serviceDetails: ServiceDetailsPageDataQuery['serviceDetailPage'];
    expertServices: ServiceDetailsPageDataQuery['expertServices'];
    workId: string;
    routeSpecifiedMpvId: string;
    printMpvId: string;
    environment: string;
    servicePrd: string;
    serviceVersion: number;
    productOptions: ProductOptionsQuery['productOptions'];
}

export function ProductModelingMainContent({
    serviceMpvId,
    productName,
    serviceDetails,
    expertServices,
    workId,
    routeSpecifiedMpvId,
    printMpvId,
    environment,
    servicePrd,
    serviceVersion,
    productOptions,
}: ProductModelingMainContentProps) {
    // get the properties for the serviceProduct
    const properties =
        expertServices.find((service) => service.serviceProduct.mpvId === serviceMpvId)
            ?.serviceProduct.properties || [];

    return (
        <>
            <Box mb="between-sections">
                <Header
                    sectionTitle={__('Design Services')}
                    name={productName}
                    heroImages={serviceDetails.heroImages}
                    description={serviceDetails.description}
                    cta={serviceDetails.primaryCta}
                    renderInclusions={() => (
                        <Inclusions
                            title={__('What you get:')}
                            inclusions={serviceDetails.inclusions}
                            properties={properties}
                        />
                    )}
                    routeSpecifiedMpvId={routeSpecifiedMpvId}
                    printMpvId={printMpvId}
                    productOptions={productOptions}
                    workId={workId}
                    environment={environment}
                    servicePrd={servicePrd}
                    serviceVersion={serviceVersion}
                />
            </Box>

            <Box mb="between-sections">
                <HowItWorks
                    title={serviceDetails.overview.title}
                    description={serviceDetails.overview.descriptionHtml}
                    image={{
                        url: serviceDetails.overview.image.cloudinaryImage.cloudinaryUrl,
                        alt: serviceDetails.overview.image.altTextOverride,
                    }}
                />
            </Box>
            {serviceDetails.inspiration && (
                <Box mb="between-sections">
                    <BoundedContent>
                        <Inspiration
                            title={serviceDetails.inspiration.title}
                            subTitle={serviceDetails.inspiration.subtitle || undefined}
                            images={serviceDetails.inspiration.designs.map((design) => {
                                return {
                                    designerAttribution: {
                                        designerName: design.designer.designerName,
                                        avatarURL: design.designer.avatarUrl,
                                    },
                                    src: design.image.cloudinaryImage.cloudinaryUrl,
                                    alt: design.image.altTextOverride,
                                };
                            })}
                        />
                    </BoundedContent>
                </Box>
            )}
            <Box mb="between-sections">
                <FAQSection faqs={serviceDetails.faqs} />
            </Box>
            <Personalisation mpvId={routeSpecifiedMpvId} />
        </>
    );
}
